// tmdb.js (~52 KB) is loaded on demand, not statically: this cache is reached
// from the Put.io store (App.vue → putio store → thumbnailCache), so a static
// import dragged all of tmdb.js into the eager entry chunk. Thumbnail lookups
// are always async, so we resolve searchThumbnail via dynamic import at each
// call site instead (browser caches the module after the first call).
import cleanMediaTitle from "@/helpers/clean-media-title.js";
import {
  parseSnapshot,
  serializeSnapshot,
  pruneEntries,
  capEntries,
} from "@/services/tmdbPersistentCache.js";

// Persisted under its own key so library/Plex cards resolve filename→poster
// instantly on reload instead of re-running searchThumbnail (3–5 TMDB searches
// per card) every cold load. Entries carry a `timestamp`; we translate that to
// the snapshot helpers' `expiresAt = timestamp + cacheExpiry` on the way out and
// back, so we can reuse the same tested prune/cap/serialize helpers.
const THUMB_STORAGE_KEY = "putio_thumb_cache_v1";

function lsOrNull() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

class ThumbnailCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    // A filename→TMDB match never changes, and a library holds far more than
    // a few hundred files — a small, short cache re-searched titles on every
    // visit. ~450 bytes/entry keeps the persisted snapshot well under its cap.
    this.maxCacheSize = 3000;
    this.cacheExpiry = 7 * 24 * 60 * 60 * 1000;
    this._persistTimer = null;
    this._hydrate();
    if (typeof window !== "undefined") {
      try {
        window.addEventListener("pagehide", () => this._persistNow());
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden") this._persistNow();
        });
      } catch {
        /* SSR / no DOM */
      }
    }
  }

  // Restore non-expired entries from localStorage into the live Map.
  _hydrate() {
    const ls = lsOrNull();
    if (!ls) return;
    try {
      const now = Date.now();
      const entries = pruneEntries(
        parseSnapshot(ls.getItem(THUMB_STORAGE_KEY)),
        now,
      );
      for (const [key, wrapped] of entries) {
        // wrapped = { value: { thumbnail, timestamp }, expiresAt }
        if (wrapped?.value && !this.cache.has(key)) {
          this.cache.set(key, wrapped.value);
        }
      }
    } catch {
      /* ignore corrupt snapshot */
    }
  }

  _persistNow() {
    const ls = lsOrNull();
    if (!ls) return;
    if (this._persistTimer) {
      clearTimeout(this._persistTimer);
      this._persistTimer = null;
    }
    try {
      const now = Date.now();
      const wrapped = [...this.cache.entries()].map(([k, e]) => [
        k,
        { value: e, expiresAt: (e?.timestamp || 0) + this.cacheExpiry },
      ]);
      const raw = serializeSnapshot(
        capEntries(pruneEntries(wrapped, now), this.maxCacheSize),
      );
      if (raw) ls.setItem(THUMB_STORAGE_KEY, raw);
      else ls.removeItem(THUMB_STORAGE_KEY);
    } catch {
      /* quota / private mode — best effort */
    }
  }

  // Debounced persist; called after cache writes.
  _schedulePersist() {
    if (this._persistTimer || typeof setTimeout === "undefined") return;
    this._persistTimer = setTimeout(() => {
      this._persistTimer = null;
      this._persistNow();
    }, 1500);
  }

  // Clear the cache (for debugging)
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
    console.log("Thumbnail cache cleared");
  }

  // Get cache statistics
  getCacheStats() {
    const stats = {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      pendingRequests: this.pendingRequests.size,
      entries: [],
    };

    for (const [key, value] of this.cache.entries()) {
      stats.entries.push({
        key,
        hasUrl: !!value.thumbnail,
        age: Date.now() - value.timestamp,
        ageMinutes: Math.round((Date.now() - value.timestamp) / 60000),
      });
    }

    return stats;
  }

  // Generate a cache key that disambiguates by year + media type.
  //
  // Two DIFFERENT titles can clean/lowercase to the same string (a 2010
  // movie and a 2020 remake, or a movie + a TV show of the same name, or
  // two unrelated files whose titles collide). Keying on the bare title
  // alone meant whichever resolved first won and every other card showed
  // its poster (#214). So we fold the year and media type into the key.
  //
  // `opts` may carry { year, mediaType }. When omitted we derive them from
  // the raw title via cleanMediaTitle so title-only callers still get a
  // stable, year/type-disambiguated bucket for the SAME raw string (the
  // year + media type are deterministic functions of the raw title, so a
  // given raw title always maps to the same key — cache hits are preserved).
  getCacheKey(rawTitle, opts = null) {
    let year = opts?.year;
    let mediaType = opts?.mediaType;

    if (year == null || mediaType == null) {
      const parsed = cleanMediaTitle(rawTitle);
      if (year == null) year = parsed.year;
      if (mediaType == null) mediaType = parsed.mediaType;
    }

    const base = rawTitle.toLowerCase().trim();
    return `${base}|${year || ""}|${mediaType || ""}`;
  }

  // Check if a thumbnail is cached and not expired
  getCached(rawTitle, opts = null) {
    const key = this.getCacheKey(rawTitle, opts);
    const cached = this.cache.get(key);

    if (!cached) return null;

    // Check if expired
    if (Date.now() - cached.timestamp > this.cacheExpiry) {
      this.cache.delete(key);
      return null;
    }

    return cached.thumbnail;
  }

  // Store thumbnail in cache
  setCached(rawTitle, thumbnail, opts = null) {
    const key = this.getCacheKey(rawTitle, opts);

    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      thumbnail,
      timestamp: Date.now(),
    });
    this._schedulePersist();
  }

  // Get thumbnail with async loading. `opts` may carry { year, mediaType }
  // to disambiguate same-titled-but-different media (see getCacheKey).
  async getThumbnail(rawTitle, opts = null) {
    const key = this.getCacheKey(rawTitle, opts);

    // Check cache first
    const cached = this.getCached(rawTitle, opts);
    if (cached) {
      return cached;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Create new request
    const request = this.fetchThumbnail(rawTitle, opts);
    this.pendingRequests.set(key, request);

    try {
      const result = await request;
      this.setCached(rawTitle, result, opts);
      return result;
    } finally {
      this.pendingRequests.delete(key);
    }
  }

  // Fetch thumbnail from TMDB with progressive fallback. When the caller
  // supplies an explicit year / mediaType (opts), prefer those over the
  // values parsed from the filename so the TMDB search is anchored to the
  // right title (collision fix #214).
  async fetchThumbnail(rawTitle, opts = null) {
    try {
      const { searchThumbnail } = await import("./tmdb.js");
      const parsed = cleanMediaTitle(rawTitle);
      const { title, variants } = parsed;
      const year = opts?.year != null ? opts.year : parsed.year;
      const mediaType =
        opts?.mediaType != null ? opts.mediaType : parsed.mediaType;

      if (!title || title.length < 2) {
        return null;
      }

      // Best low-score result seen across the variants of THIS title. Must be
      // a local — it used to live on `this.fallbackResult`, shared across all
      // concurrent fetchThumbnail calls, so under a shelf-load burst one
      // title's fallback poster could be returned (and cached) for another.
      let fallbackResult = null;

      // Try each variant in order until we find a good match
      for (let i = 0; i < variants.length; i++) {
        const searchTitle = variants[i];
        if (!searchTitle || searchTitle.length < 2) continue;

        const result = await searchThumbnail(searchTitle, year, mediaType);

        // If we found a result with a good score, use it
        if (result?.url) {
          // For the first (conservative) variant, accept any result
          // For later variants, only accept results with decent scores
          if (i === 0 || (result.score && result.score >= 6)) {
            return result.url;
          }

          // Store the result as a fallback in case later attempts fail
          if (!fallbackResult || result.score > fallbackResult.score) {
            fallbackResult = result;
          }
        }
      }

      // If no good results found, use the best fallback result if available
      if (fallbackResult?.url) {
        return fallbackResult.url;
      }

      return null;
    } catch (error) {
      console.error("Error fetching thumbnail for:", rawTitle, error);
      return null;
    }
  }

  // Preload thumbnails for multiple items
  async preloadThumbnails(rawTitles) {
    const promises = rawTitles.map((title) => this.getThumbnail(title));
    return Promise.allSettled(promises);
  }

  // Get thumbnail synchronously from cache only (no API call)
  getThumbnailSync(rawTitle, opts = null) {
    return this.getCached(rawTitle, opts);
  }

  // Async match-resolver. Same TMDB search as getThumbnail but
  // returns the full match object { id, url, mediaType, title, year }
  // instead of just the URL. Callers route shelf cards to
  // /browse/<mediaType>/<id> on match so clicks land on the details
  // page (with Play on Plex / Play on Put.io buttons) instead of
  // skipping straight to the player. Caches under a separate key so
  // it doesn't collide with getThumbnail's URL-only cache.
  async getMatch(rawTitle, opts = null) {
    if (!rawTitle) return null;
    // Key the match cache by title + year + media type — NOT the bare title.
    // Before #214 two different titles that lowercased to the same string
    // shared one `match::<title>` entry, so whichever resolved first won and
    // the other card showed the wrong poster. Folding year + type into the
    // key (via getCacheKey) keeps same-named-but-different media apart while
    // still giving a stable bucket for the same raw title.
    const key = `match::${this.getCacheKey(rawTitle, opts)}`;
    const cached = this.cache.get(key);
    if (cached) {
      if (Date.now() - cached.timestamp <= this.cacheExpiry) {
        return cached.thumbnail; // we stored the match object here
      }
      this.cache.delete(key);
    }
    try {
      const { searchThumbnail } = await import("./tmdb.js");
      const parsed = cleanMediaTitle(rawTitle);
      const { title, variants } = parsed;
      const year = opts?.year != null ? opts.year : parsed.year;
      const mediaType =
        opts?.mediaType != null ? opts.mediaType : parsed.mediaType;
      if (!title || title.length < 2) return null;
      let best = null;
      for (const v of variants) {
        if (!v) continue;
        const r = await searchThumbnail(v, year, mediaType);
        if (r && r.id) {
          if (!best || (r.score || 0) > (best.score || 0)) best = r;
          if ((r.score || 0) >= 8) break; // good enough; stop searching variants
        }
      }
      // Prefix-retry: mangled saved titles ("SLC Punk DL worldmkv",
      // "The Green Planet iP DL", "Perfect Planet The Sun") return nothing for
      // the full string. Drop trailing words and retry — searchThumbnail's
      // relevance gate rejects irrelevant prefixes, so we stop at the first real
      // match. Bounded to a few attempts to cap TMDB calls.
      if (!best) {
        const words = title.split(/\s+/).filter(Boolean);
        let tries = 0;
        for (
          let n = words.length - 1;
          n >= 2 && !best && tries < 4;
          n--, tries++
        ) {
          const r = await searchThumbnail(
            words.slice(0, n).join(" "),
            year,
            mediaType,
          );
          if (r && r.id) best = r;
        }
      }
      if (this.cache.size >= this.maxCacheSize) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }
      this.cache.set(key, { thumbnail: best, timestamp: Date.now() });
      this._schedulePersist();
      return best;
    } catch (err) {
      console.warn("[thumbnailCache] getMatch failed:", err?.message ?? err);
      return null;
    }
  }

  // Check if thumbnail is currently being loaded
  isLoading(rawTitle, opts = null) {
    const key = this.getCacheKey(rawTitle, opts);
    return this.pendingRequests.has(key);
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Get cache stats
  getCacheStats() {
    return {
      size: this.cache.size,
      pending: this.pendingRequests.size,
      maxSize: this.maxCacheSize,
    };
  }
}

// Export singleton instance
export default new ThumbnailCache();
